/**
 * Tech-debt #4 — TriggerMessage (OCPP 1.6J §4.x / 2.0.1). The CSMS asks the CP
 * to send a specific message now; we actually fire it (Heartbeat,
 * StatusNotification, MeterValues, BootNotification, LogStatusNotification).
 */
import { OcppV201Adapter } from '@/protocol/v201/OcppV201Adapter';
import { KEY_CP_MODEL, KEY_CP_VENDOR } from '@/protocol/v16/OcppV16Adapter';
import { useConfigurationStore } from '../useConfigurationStore';
import { useStationStore, type ConnectorState } from '../useStationStore';
import type { CsmsCtx, CsmsEvent } from './context';

export function handleTriggerMessage(event: CsmsEvent<'TriggerMessage'>, ctx: CsmsCtx): void {
  const { adapter, dispatcher, messageId } = ctx;
  const store = useStationStore.getState();
  const trigger = event.requestedMessage;
  let handled = false;
  switch (trigger) {
    case 'Heartbeat':
      void store.heartbeat();
      handled = true;
      break;
    case 'StatusNotification': {
      const targets =
        event.connectorId !== undefined
          ? [store.connectors.get(event.connectorId)].filter(
              (c): c is ConnectorState => c !== undefined,
            )
          : [...store.connectors.values()];
      for (const c of targets) {
        void adapter.statusNotification({ connectorId: c.id, status: c.status }).response;
      }
      handled = true;
      break;
    }
    case 'MeterValues':
      void store.emitMeterValuesForActiveTx();
      handled = true;
      break;
    case 'BootNotification': {
      // BUG-027 / Session 8f.15 — actually re-fire BootNotification using the
      // same vendor/model the boot path reads so the wire payload matches.
      const cfg = useConfigurationStore.getState().entries;
      const chargePointVendor =
        cfg.find((e) => e.key === KEY_CP_VENDOR)?.value ?? 'VirtualCharger';
      const chargePointModel = cfg.find((e) => e.key === KEY_CP_MODEL)?.value ?? 'sim';
      void adapter.bootNotification({ chargePointVendor, chargePointModel }).response;
      handled = true;
      break;
    }
    case 'LogStatusNotification': {
      // Session 19e — v2.0.1-only. Fire a synthetic Idle status so the CSMS can
      // verify the trigger path.
      if (adapter instanceof OcppV201Adapter) {
        void adapter.logStatusNotification({ status: 'Idle' }).response;
        handled = true;
      }
      break;
    }
    default:
      handled = false;
  }
  dispatcher.reply(messageId, { status: handled ? 'Accepted' : 'NotImplemented' });
}
